import { selectEntitlements } from '../selectors/userContext.selectors';
import { useCapacityFeatureFlags } from './useCapacityFeatureFlags';
import { useSelector } from 'react-redux';
import React from 'react';
import type { Subscription } from '@innovyze/shared-utils';

// Valid use case of {}. This allows to express the type `${Subscription}` OR any other string.
// eslint-disable-next-line @typescript-eslint/ban-types
type Entitlement = `${Subscription}` | (string & {});

export type RestrictedFeature = typeof RESTRICTED_FEATURES[number];

/**
 * Mappings between restricted features and their required entitlements.
 */
const RESTRICTED_FEATURES = [
	{ name: 'I360A-ASSET-GROUP-SET', requiredEntitlements: ['I360ADVRU'] },
	{ name: 'I360A-DETERIORATION', requiredEntitlements: ['I360ADVRU'] },
	{ name: 'I360A-FAILURE', requiredEntitlements: ['I360ADVRU'] },
	{ name: 'I360A-REHAB', requiredEntitlements: ['I360ADVRU'] },
	{ name: 'I360I-ALERTS', requiredEntitlements: ['I360IADVRU'] },
	{ name: 'I360I-DATA', requiredEntitlements: ['I360IADVRU'] },
	{ name: 'I360I-DEMAND-FORECAST', requiredEntitlements: ['I360IADVRU'] },
	{ name: 'I360I-FACILITIES', requiredEntitlements: ['I360IADVRU'] },
	{ name: 'I360I-FORMS', requiredEntitlements: ['I360IADVRU'] },
	{ name: 'I360I-ILI', requiredEntitlements: ['I360IADVRU'] },
	{ name: 'I360I-IMAGES', requiredEntitlements: ['I360IADVRU'] },
	{ name: 'I360I-INCIDENTS-MANAGER', requiredEntitlements: ['I360IADVRU'] },
	{ name: 'I360I-PUMP-STATIONS', requiredEntitlements: ['I360IADVRU'] },
	{ name: 'I360I-REPORTS', requiredEntitlements: ['I360IADVRU'] },
] as const; // satisfies { name: string, requiredEntitlements: Entitlement[] }[];
// TODO: Uncomment line above when typescript version gets updated.

export type EntitlementUtils = {
	/**
	 * An array of entitlements owned by the user.
	 */
	entitlements: Entitlement[];
	/**
	 * Checks if user has one or more entitlements.
	 *
	 * @param entitlement The entitlement that we want to check.
	 * @return Wether the user has the entitlements or not.
	 */
	hasEntitlement(entitlement: Entitlement | Entitlement[]): boolean;
	/**
	 * Checks if user has access to a feature based on its entitlements.
	 *
	 * @param feature The feature we want to check.
	 * @returns Wether the user has access to the feature or not.
	 */
	// TODO: Switch the type definition once RESTRICTED_FEATURES doesn't depend on feature flag.
	// hasAccessToFeature(feature: (typeof RESTRICTED_FEATURES)[number]['name'] | (string & {})): boolean;
	hasAccessToFeature(
		// Valid use case of {}. This allows to express the type RestrictedFeature['name'] OR any other string.
		// eslint-disable-next-line @typescript-eslint/ban-types
		feature: RestrictedFeature['name'] | (string & {}),
	): boolean;
};

/** A set of utilities to derive data from user entitlements. */
export function useEntitlements(): EntitlementUtils {
	const entitlements = useSelector(selectEntitlements);
	const { isAdvancedEntitlementFeatureEnabled } = useCapacityFeatureFlags();

	const hasEntitlement: EntitlementUtils['hasEntitlement'] =
		React.useCallback(
			entitlement => {
				const e = Array.isArray(entitlement)
					? entitlement
					: [entitlement];
				return e.some(a => entitlements.includes(a));
			},
			[entitlements],
		);

	const hasAccessToFeature: EntitlementUtils['hasAccessToFeature'] =
		React.useCallback(
			feature => {
				if (!isAdvancedEntitlementFeatureEnabled) return true;
				const f = RESTRICTED_FEATURES.find(a => a.name === feature);
				if (typeof f === 'undefined') return true;
				const requiredEntitlements = [...f.requiredEntitlements];
				return hasEntitlement(requiredEntitlements);
			},
			[hasEntitlement, isAdvancedEntitlementFeatureEnabled],
		);

	return { entitlements, hasAccessToFeature, hasEntitlement };
}
